import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users, ArrowRight, Home } from "lucide-react";
import { toast } from "sonner";

interface HouseholdSetupProps {
  onCreateHousehold: (name: string) => Promise<unknown>;
}

const HouseholdSetup = ({ onCreateHousehold }: HouseholdSetupProps) => {
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || creating) return;

    setCreating(true);
    try {
      await onCreateHousehold(name.trim());
      toast.success("Household created successfully.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to create household.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-6">
      <div className="glass-card max-w-md w-full p-8 space-y-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto">
          <Home className="w-8 h-8 text-primary" />
        </div>

        <div className="space-y-2">
          <h2 className="font-heading font-bold text-xl text-foreground">Set Up Your Household</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Create a household to invite your children, track their progress, and manage your family subscription.
          </p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            HeirWise is designed for learners ages 13 and older.
          </p>
        </div>

        <form onSubmit={handleCreate} className="space-y-4 text-left">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Household Name</Label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. The Smith Family"
              required
              className="h-10"
            />
          </div>

          <Button type="submit" disabled={creating || !name.trim()} className="w-full gap-2">
            {creating ? "Creating..." : "Create Household"}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </form>

        <div className="border-t border-border pt-4">
          <div className="flex items-start gap-3 text-left">
            <Users className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                After creating your household, you can invite children by email. They'll get a code to enter when signing up.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HouseholdSetup;
